import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@attentive-platform/stem-ui';

import { SearchOmniboxStyles as S } from './styles';

const flattenCatalog = trades => {
  const rows = [];
  trades.forEach((t, tradeIdx) => {
    (t.scopes || []).forEach(sc => {
      const elements = sc.elements || [];
      if (!elements.length) return;
      rows.push({
        kind: 'Item',
        label: sc.scope_name,
        path: t.trade_name + (sc.section_name ? ` · ${sc.section_name}` : ''),
        tradeId: t.trade_id,
        scopeId: sc.scope_id,
        tradeIdx
      });
      elements.forEach(el => {
        rows.push({
          kind: 'Element',
          label: el.name,
          path: `${t.trade_name} · ${sc.scope_name}`,
          tradeId: t.trade_id,
          scopeId: sc.scope_id,
          elementId: el.id,
          tradeIdx
        });
      });
    });
  });
  return rows;
};

const SearchOmnibox = ({ trades, draft, onPick }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapRef = useRef(null);

  const rows = useMemo(() => flattenCatalog(trades), [trades]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return rows.filter(r => `${r.label} ${r.path}`.toLowerCase().indexOf(q) >= 0).slice(0, 20);
  }, [rows, query]);

  const catalogSize = rows.length;

  useEffect(() => {
    setCursor(0);
  }, [query]);

  useEffect(() => {
    const handler = e => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = row => {
    if (!row) return;
    onPick(row);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = e => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(c => Math.min(results.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(results[cursor]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showNoResults = open && query.trim() && results.length === 0;
  const showPanel = open && results.length > 0;

  return (
    <S.Wrap ref={wrapRef} onKeyDown={onKeyDown}>
      <Input
        size='large'
        value={query}
        placeholder='Search any item or element'
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        width='20rem'
      />
      {showPanel && (
        <S.Panel>
          <S.PanelLabel>
            {results.length} match{results.length === 1 ? '' : 'es'}
          </S.PanelLabel>
          {results.map((r, i) => {
            const active = i === cursor;
            const checked = r.kind === 'Element' ? !!draft.elements[r.elementId] : !!draft.scopes[r.scopeId];
            const Row = active ? S.RowActive : S.RowIdle;
            return (
              <Row
                key={`${r.kind}-${r.elementId || r.scopeId}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => pick(r)}>
                <S.RowTop>
                  <S.KindBadge>{r.kind}</S.KindBadge>
                  <S.RowLabel>{r.label}</S.RowLabel>
                  {checked && <S.CheckedPill>in your scope</S.CheckedPill>}
                </S.RowTop>
                <S.RowPath>{r.path}</S.RowPath>
              </Row>
            );
          })}
          <S.Footer>
            <S.HintText>↑ ↓ to move · Enter to open · Esc to close</S.HintText>
            <S.HintText>{catalogSize} in catalog</S.HintText>
          </S.Footer>
        </S.Panel>
      )}
      {showNoResults && (
        <S.NoResultsPanel>
          <S.NoResultsHeading>Nothing matches &ldquo;{query}&rdquo;</S.NoResultsHeading>
          <S.NoResultsHint>Try a shorter word, or add it to your trade note at the end of the trade.</S.NoResultsHint>
        </S.NoResultsPanel>
      )}
    </S.Wrap>
  );
};

export default SearchOmnibox;
